import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  TextField,
  Typography,
  Container,
  Box,
  Alert,
  CircularProgress,
  Link,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
  FormLabel,
  Divider,
  InputAdornment
} from '@mui/material';
import { API_BASE_URL } from '../config';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';

function Login() {
  const [countryCode, setCountryCode] = useState<string>('+852');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState<number>(0);
  const [pageSelection, setPageSelection] = useState<'user-home' | 'owned-site'>('user-home');
  // const [userSites, setUserSites] = useState<Array<{_id: string, name: string, route: string, role: string}>>([]);

  // Combined phone number with country code and formatting
  const formatPhoneNumber = (code: string, number: string) => {
    if (!number) return code;
    
    // Format Hong Kong numbers: +852 2345 6789
    if (code === '+852') {
      const cleaned = number.replace(/\s/g, '');
      if (cleaned.length <= 4) {
        return `${code} ${cleaned}`;
      } else if (cleaned.length <= 8) {
        return `${code} ${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
      } else {
        return `${code} ${cleaned.slice(0, 4)} ${cleaned.slice(4, 8)}`;
      }
    }
    
    return `${code}${number}`;
  };

  const fullPhoneNumber = formatPhoneNumber(countryCode, phoneNumber);

  const { sendOtp, loginWithOtp } = useUser();
  const navigate = useNavigate();

  // Check if user has owned sites after successful login
  const checkUserSites = async (token: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/site-admin/my-sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sites = response.data?.sites || [];
      
      // Return first site if available
      if (sites.length > 0) {
        return sites[0]; // Return first site for navigation
      }
      
      // User has no owned sites
      return { error: 'no_sites' };
    } catch (error: any) {
      console.error('Error checking user sites:', error);
      // If 403, user has no sites
      if (error.response?.status === 403) {
        return { error: 'no_sites' };
      }
      // Other errors
      return { error: 'api_error' };
    }
  };

  const handleSendVerificationCode = async () => {
    if (!phoneNumber) {
      setError('請輸入電話號碼');
      return;
    }

    // Validate local number for the selected country
    if (countryCode === '+852') {
      // Check if phone number is exactly 8 digits and starts with 2-9
      if (phoneNumber.length !== 8 || !/^[2-9]/.test(phoneNumber)) {
        setError('請提供有效的香港電話號碼（8位數字，首位為2-9）');
        return;
      }
    } else {
      // Currently only HK is supported end-to-end
      setError('目前僅支援香港電話號碼');
      return;
    }

    setIsSendingCode(true);
    setError('');
    try {
      // Send clean phone number without formatting to backend
      const cleanPhoneNumber = `${countryCode}${phoneNumber}`;
      await sendOtp(cleanPhoneNumber);
      setSuccess('驗證碼已發送到您的 WhatsApp');
      setCodeSent(true);
      setResendSecondsLeft(30);
    } catch (err: any) {
      setError(err.response?.data?.message || '發送驗證碼失敗');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyAndLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!phoneNumber || !verificationCode) {
      setError('請輸入電話號碼和驗證碼');
      return;
    }

    // Validate again before submit
    if (countryCode === '+852') {
      // Check if phone number is exactly 8 digits and starts with 2-9
      if (phoneNumber.length !== 8 || !/^[2-9]/.test(phoneNumber)) {
        setError('請提供有效的香港電話號碼（8位數字，首位為2-9）');
        return;
      }
    } else {
      setError('目前僅支援香港電話號碼');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      // Send clean phone number without formatting to backend
      const cleanPhoneNumber = `${countryCode}${phoneNumber}`;
      const result: any = await loginWithOtp(cleanPhoneNumber, verificationCode);
      
      // Check if user wants to go to owned site
      console.log(pageSelection);
      if (pageSelection === 'owned-site') {
        const ownedSite = await checkUserSites(result.token);
        console.log('ownedSite:', ownedSite);
        if (ownedSite && ownedSite.error) {
          // Handle error cases
          if (ownedSite.error === 'no_sites') {
            navigate('/');
            setIsLoading(false);
            return;
          } else {
            setError('無法檢查場地權限，請稍後再試。');
            setIsLoading(false);
            return;
          }
        } else if (ownedSite && ownedSite.route) {
          // Successfully found owned site
          navigate(`/${ownedSite.route}`);
          return;
        }
      }
      
      // Default navigation: go to user home
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || '登入失敗');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (resendSecondsLeft <= 0) return;
    const interval = setInterval(() => {
      setResendSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendSecondsLeft]);

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5">
          登入
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2, width: '100%' }}>{success}</Alert>}

        <Box component="form" onSubmit={handleVerifyAndLogin} sx={{ width: '100%', mt: 3 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            label="電話號碼"
            placeholder="+852 9876 5432"
            value={fullPhoneNumber}
            onChange={(e) => {
              const value = e.target.value;
              let newCountryCode = countryCode;
              let newPhoneNumber = phoneNumber;
              
              // Handle country code selection
              if (value.startsWith('+852')) {
                newCountryCode = '+852';
                newPhoneNumber = value.substring(4).replace(/\s/g, '');
              } else if (value.startsWith('+')) {
                // For other country codes, keep the current logic
                newCountryCode = '+852';
                newPhoneNumber = value.substring(4).replace(/\s/g, '');
              } else {
                // If no country code, assume HK
                newCountryCode = '+852';
                newPhoneNumber = value.replace(/\s/g, '');
              }
              
              // Only allow digits for phone number
              newPhoneNumber = newPhoneNumber.replace(/\D/g, '');
              
              // Limit to 8 digits for Hong Kong
              if (newPhoneNumber.length <= 8) {
                setCountryCode(newCountryCode);
                setPhoneNumber(newPhoneNumber);
              }
              
              // Clear error when user starts typing
              if (error) {
                setError('');
              }
            }}
            disabled={isLoading || isSendingCode}
            helperText="請輸入完整的電話號碼，包含國家代碼"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <span style={{ fontSize: '1.2rem' }}>🇭🇰</span>
                </InputAdornment>
              ),
            }}
          />

          {!codeSent ? (
            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={handleSendVerificationCode}
              disabled={isSendingCode || isLoading}
            >
              {isSendingCode ? <CircularProgress size={24} color="inherit" /> : '發送驗證碼'}
            </Button>
          ) : (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                label="驗證碼"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={isLoading}
                helperText="請檢查您的 WhatsApp 訊息"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : '登入'}
              </Button>

              <Button
                type="button"
                fullWidth
                variant="text"
                sx={{ mt: 1 }}
                onClick={handleSendVerificationCode}
                disabled={isSendingCode || isLoading || resendSecondsLeft > 0}
              >
                {resendSecondsLeft > 0
                  ? `重新發送驗證碼（${resendSecondsLeft}s）`
                  : '重新發送驗證碼'}
              </Button>
            </>
          )}

          {/* Page Selection Toggle Buttons */}
          <Divider sx={{ my: 3 }} />
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              登入後前往
            </FormLabel>
            <ToggleButtonGroup
              value={pageSelection}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setPageSelection(newValue);
                }
              }}
              sx={{ width: '100%' }}
            >
              <ToggleButton value="user-home" sx={{ flex: 1 }}>
                個人主頁
              </ToggleButton>
              <ToggleButton value="owned-site" sx={{ flex: 1 }}>
                場主主頁
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              <Link
                component={RouterLink}
                to="/signup"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                立即註冊您的群組
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              曾經報名？請使用之前的電話號碼直接登入。
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;